import type { Hub, Post } from '@/types'
import type { ToolMeta } from '@/constants/tools'

import { siteConfig } from '@/constants/site'

type JsonLdNode = Record<string, unknown>

const ORG_ID = `${siteConfig.url}/#organization`
const WEBSITE_ID = `${siteConfig.url}/#website`
const DEFAULT_IMAGE = '/og.png'

export interface BreadcrumbEntry {
    name: string
    path?: string
}

export interface FaqEntry {
    question: string
    answer: string
}

/** Resolve a path or partial URL to an absolute URL on the canonical domain. */
export function absoluteUrl(path = ''): string {
    if (!path) return siteConfig.url
    if (path.startsWith('http')) return path

    return `${siteConfig.url}${path.startsWith('/') ? path : `/${path}`}`
}

/** Wrap one or more nodes in a schema.org graph document. */
export function graph(nodes: JsonLdNode[]): JsonLdNode {
    return {
        '@context': 'https://schema.org',
        '@graph': nodes
    }
}

export function organizationSchema(): JsonLdNode {
    return {
        '@type': 'Organization',
        '@id': ORG_ID,
        name: siteConfig.name,
        url: siteConfig.url,
        description: siteConfig.description,
        slogan: siteConfig.tagline ?? undefined,
        logo: {
            '@type': 'ImageObject',
            url: absoluteUrl('/logo.svg'),
            contentUrl: absoluteUrl('/logo.svg')
        },
        image: absoluteUrl(DEFAULT_IMAGE),
        knowsAbout: [
            'Cosmetic dentistry',
            'Dental health',
            'General dentistry',
            'Oral hygiene',
            'Teeth whitening',
            'Dental veneers',
            'Preventive dental care'
        ]
    }
}

export function websiteSchema(): JsonLdNode {
    return {
        '@type': 'WebSite',
        '@id': WEBSITE_ID,
        name: siteConfig.name,
        alternateName: siteConfig.tagline ?? undefined,
        url: siteConfig.url,
        description: siteConfig.description,
        inLanguage: 'en',
        publisher: { '@id': ORG_ID }
    }
}

export function breadcrumbSchema(items: BreadcrumbEntry[]): JsonLdNode {
    return {
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            ...(item.path ? { item: absoluteUrl(item.path) } : {})
        }))
    }
}

export function itemListSchema(name: string, entries: { name: string; path: string }[]): JsonLdNode {
    return {
        '@type': 'ItemList',
        name,
        itemListElement: entries.map((entry, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: entry.name,
            url: absoluteUrl(entry.path)
        }))
    }
}

export function articleSchema(post: Post): JsonLdNode {
    const url = absoluteUrl(`/${post.hubSlug}/${post.slug}`)
    const image = absoluteUrl(post.coverImage ?? DEFAULT_IMAGE)
    const modified = post.updatedAt ?? post.publishedAt

    const author = post.authorName
        ? {
              '@type': 'Person',
              name: post.authorName,
              ...(post.authorRole ? { jobTitle: post.authorRole } : {}),
              ...(post.authorCredentials ? { description: post.authorCredentials } : {})
          }
        : { '@id': ORG_ID }

    const node: JsonLdNode = {
        '@type': 'Article',
        '@id': `${url}#article`,
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        url,
        headline: post.title,
        description: post.excerpt,
        image: [image],
        inLanguage: 'en',
        isAccessibleForFree: true,
        datePublished: post.publishedAt,
        dateModified: modified,
        articleSection: post.hubName,
        author,
        publisher: { '@id': ORG_ID },
        isPartOf: { '@id': WEBSITE_ID }
    }

    if (post.tags.length > 0) node.keywords = post.tags.join(', ')

    if (post.reviewedBy) {
        node.reviewedBy = { '@type': 'Person', name: post.reviewedBy }
        node.lastReviewed = modified
    }

    return node
}

export function hubCollectionSchema(hub: Hub, posts: Post[]): JsonLdNode {
    const url = absoluteUrl(`/${hub.slug}`)

    return {
        '@type': 'CollectionPage',
        '@id': `${url}#collection`,
        url,
        name: hub.title,
        headline: hub.title,
        description: hub.description,
        inLanguage: 'en',
        isPartOf: { '@id': WEBSITE_ID },
        about: { '@type': 'Thing', name: hub.name },
        mainEntity: {
            '@type': 'ItemList',
            itemListElement: posts.map((post, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                url: absoluteUrl(`/${post.hubSlug}/${post.slug}`),
                name: post.title
            }))
        }
    }
}

export function aboutPageSchema(description: string): JsonLdNode {
    const url = absoluteUrl('/about')

    return {
        '@type': 'AboutPage',
        '@id': `${url}#about`,
        url,
        name: `About ${siteConfig.name}`,
        description,
        inLanguage: 'en',
        isPartOf: { '@id': WEBSITE_ID },
        about: { '@id': ORG_ID },
        mainEntity: { '@id': ORG_ID }
    }
}

export function privacyPageSchema(description: string, lastReviewed: string): JsonLdNode {
    const url = absoluteUrl('/privacy')

    return {
        '@type': 'WebPage',
        '@id': `${url}#privacy`,
        url,
        name: `Privacy & Cookie Policy | ${siteConfig.name}`,
        description,
        inLanguage: 'en',
        isPartOf: { '@id': WEBSITE_ID },
        about: { '@id': ORG_ID },
        publisher: { '@id': ORG_ID },
        dateModified: lastReviewed
    }
}

export function faqSchema(items: FaqEntry[]): JsonLdNode {
    return {
        '@type': 'FAQPage',
        mainEntity: items.map(item => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer
            }
        }))
    }
}

/** A free, browser-based tool page (calculator, checker, quiz). */
export function toolSchema(tool: ToolMeta): JsonLdNode {
    const url = absoluteUrl(tool.path)

    return {
        '@type': 'WebApplication',
        '@id': `${url}#tool`,
        url,
        name: tool.name,
        description: tool.description,
        applicationCategory: tool.schemaCategory,
        operatingSystem: 'Any',
        browserRequirements: 'Requires JavaScript',
        isAccessibleForFree: true,
        inLanguage: 'en',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        publisher: { '@id': ORG_ID },
        isPartOf: { '@id': WEBSITE_ID }
    }
}
